import React, { useState } from 'react';
import {
  Alert,
  Button,
  Content,
  Flex,
  FlexItem,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  TextInput,
} from '@patternfly/react-core';
import { Table, Tbody, Th, Thead, Tr } from '@patternfly/react-table';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useProductCategories } from '../../../hooks/useProductCategories';
import type {
  ApiResponseProductCategory,
  ApiResponseProductCategoryDeleted,
} from '../../../types/api/ApiResponseAdminProduct';
import ProductCategoryRow, { type CategoryMutationResult } from './ProductCategoryRow';

const NAME_MAX = 64;

interface ManageCategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Product counts per category id (from the grid) — blocks deleting a category that's still in use. */
  categoryUsage: Map<number, number>;
  /** Called on close if any category changed, so the grid re-fetches (a rename changes group headers). */
  onChanged: () => void;
}

// Create / rename / delete product categories. Mounted only while open (fresh state). Each mutation
// branches on the JSend body.status, notifies + opens the drawer on both outcomes, then refetches the
// list in place. A rename/create/delete is reported to the grid on close so its category groups refresh.
function ManageCategoriesModal({ isOpen, onClose, categoryUsage, onChanged }: ManageCategoriesModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { categories, loading, error, refetch } = useProductCategories(isOpen);

  const [addDraft, setAddDraft] = useState('');
  const [addServerError, setAddServerError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const addTrimmed = addDraft.trim();
  const isAddNameValid = addTrimmed.length > 0 && addTrimmed.length <= NAME_MAX;
  const addHelperError =
    addServerError ?? (addTrimmed.length > NAME_MAX ? `Name must be at most ${NAME_MAX} characters.` : null);

  const handleClose = () => {
    // A rename changes the category name embedded in product rows, so the grid's group headers are
    // stale until it re-fetches — trigger that once, on close, if anything changed.
    if (hasChanges) {
      onChanged();
    }
    onClose();
  };

  const renameCategory = async (id: number, name: string): Promise<CategoryMutationResult> => {
    try {
      const response = await apiFetch(`/admin/products/categories/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      });
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseProductCategory | null;
      if (body?.status === 'success' && body.data?.category) {
        addNotification({
          title: 'Category renamed',
          srTitle: 'Success alert',
          description: `The category is now "${body.data.category.name}".`,
          variant: 'success',
        });
        setDrawerOpen(true);
        setHasChanges(true);
        await refetch();
        return { ok: true, fieldError: null };
      }
      const fieldError = body?.data?.name ?? null;
      addNotification({
        title: 'Could not rename category',
        srTitle: 'Error alert',
        description: fieldError ?? body?.message ?? 'Could not rename the category. Please try again.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false, fieldError };
    } catch (err) {
      addNotification({
        title: 'Could not rename category',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false, fieldError: null };
    }
  };

  const deleteCategory = async (id: number): Promise<{ ok: boolean }> => {
    try {
      const response = await apiFetch(`/admin/products/categories/${id}`, { method: 'DELETE' });
      const body = (await response.json().catch(() => null)) as ApiResponseProductCategoryDeleted | null;
      if (body?.status === 'success' && body.data?.deleted) {
        addNotification({
          title: 'Category deleted',
          srTitle: 'Success alert',
          description: `The category "${body.data.deleted.name}" was deleted.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        setHasChanges(true);
        await refetch();
        return { ok: true };
      }
      // An in-use delete is rejected with the reason under data.category (the row pre-disables this,
      // but a race is still possible).
      const reason = body?.data?.category ?? body?.message ?? 'Could not delete the category. Please try again.';
      addNotification({
        title: 'Could not delete category',
        srTitle: 'Error alert',
        description: reason,
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false };
    } catch (err) {
      addNotification({
        title: 'Could not delete category',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false };
    }
  };

  const handleAdd = async () => {
    if (!isAddNameValid) {
      return;
    }
    setIsAdding(true);
    setAddServerError(null);
    try {
      const response = await apiFetch('/admin/products/categories', {
        method: 'POST',
        body: JSON.stringify({ name: addTrimmed }),
      });
      const body = (await response.json().catch(() => null)) as ApiResponseProductCategory | null;
      if (body?.status === 'success' && body.data?.category) {
        addNotification({
          title: 'Category created',
          srTitle: 'Success alert',
          description: `The category "${body.data.category.name}" was created.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        setHasChanges(true);
        setAddDraft('');
        await refetch();
      } else {
        const fieldError = body?.data?.name ?? null;
        setAddServerError(fieldError);
        addNotification({
          title: 'Could not create category',
          srTitle: 'Error alert',
          description: fieldError ?? body?.message ?? 'Could not create the category. Please try again.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      }
    } catch (err) {
      addNotification({
        title: 'Could not create category',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsAdding(false);
    }
  };

  const renderBody = () => {
    if (loading && categories.length === 0) {
      return (
        <Flex justifyContent={{ default: 'justifyContentCenter' }} style={{ padding: '2rem' }}>
          <FlexItem>
            <Spinner aria-label="Loading categories" />
          </FlexItem>
        </Flex>
      );
    }

    if (error && categories.length === 0) {
      return (
        <Alert variant="danger" isInline title="Could not load categories">
          {error}
        </Alert>
      );
    }

    return (
      <>
        <Table aria-label="Product categories" variant="compact">
          <Thead>
            <Tr>
              <Th>Category</Th>
              <Th>Usage</Th>
              <Th screenReaderText="Row actions" />
            </Tr>
          </Thead>
          <Tbody>
            {categories.map((category) => (
              <ProductCategoryRow
                key={category.id}
                category={category}
                usageCount={categoryUsage.get(category.id) ?? 0}
                onRename={renameCategory}
                onDelete={deleteCategory}
              />
            ))}
          </Tbody>
        </Table>

        <div className="pf-v6-u-mt-lg">
          <Content component="h3" className="pf-v6-u-mb-sm">
            Add a category
          </Content>
          <Flex alignItems={{ default: 'alignItemsFlexStart' }} spaceItems={{ default: 'spaceItemsSm' }}>
            <FlexItem grow={{ default: 'grow' }}>
              <TextInput
                id="admin-category-add-name"
                type="text"
                value={addDraft}
                onChange={(_event, value) => {
                  setAddDraft(value);
                  if (addServerError) {
                    setAddServerError(null);
                  }
                }}
                validated={addHelperError ? 'error' : 'default'}
                isDisabled={isAdding}
                aria-label="New category name"
                placeholder="Category name"
              />
            </FlexItem>
            <FlexItem>
              <Button
                variant="secondary"
                onClick={() => void handleAdd()}
                isLoading={isAdding}
                isDisabled={isAdding || !isAddNameValid}
              >
                Add
              </Button>
            </FlexItem>
          </Flex>
          {addHelperError && (
            <HelperText>
              <HelperTextItem variant="error">{addHelperError}</HelperTextItem>
            </HelperText>
          )}
        </div>
      </>
    );
  };

  return (
    <Modal variant="medium" isOpen={isOpen} onClose={handleClose} aria-label="Manage product categories">
      <ModalHeader title="Manage categories" />
      <ModalBody tabIndex={0} aria-label="Product categories">
        {renderBody()}
      </ModalBody>
      <ModalFooter>
        <Button variant="link" onClick={handleClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default ManageCategoriesModal;
